/**
 * CIPAA (CIPA + Assedio) Election Types
 *
 * Type definitions for the CIPAA election module.
 *
 * @version 2025.1.0
 */

// ========== ENUMS ==========

export enum CipaaStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  REGISTRATION_OPEN = 'REGISTRATION_OPEN',
  REGISTRATION_CLOSED = 'REGISTRATION_CLOSED',
  VOTING_OPEN = 'VOTING_OPEN',
  VOTING_CLOSED = 'VOTING_CLOSED',
  COUNTING = 'COUNTING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum CipaaCandidateStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  WITHDRAWN = 'WITHDRAWN',
}

export enum CipaaCandidatePosition {
  TITULAR = 'TITULAR',
  SUPLENTE = 'SUPLENTE',
}

// ========== LABELS ==========

export const CIPAA_STATUS_LABELS: Record<CipaaStatus, string> = {
  [CipaaStatus.DRAFT]: 'Rascunho',
  [CipaaStatus.SCHEDULED]: 'Agendada',
  [CipaaStatus.REGISTRATION_OPEN]: 'Inscrições Abertas',
  [CipaaStatus.REGISTRATION_CLOSED]: 'Inscrições Encerradas',
  [CipaaStatus.VOTING_OPEN]: 'Votação Aberta',
  [CipaaStatus.VOTING_CLOSED]: 'Votação Encerrada',
  [CipaaStatus.COUNTING]: 'Apuração',
  [CipaaStatus.COMPLETED]: 'Concluída',
  [CipaaStatus.CANCELLED]: 'Cancelada',
};

export const CIPAA_STATUS_COLORS: Record<CipaaStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [CipaaStatus.DRAFT]: 'default',
  [CipaaStatus.SCHEDULED]: 'info',
  [CipaaStatus.REGISTRATION_OPEN]: 'primary',
  [CipaaStatus.REGISTRATION_CLOSED]: 'warning',
  [CipaaStatus.VOTING_OPEN]: 'success',
  [CipaaStatus.VOTING_CLOSED]: 'warning',
  [CipaaStatus.COUNTING]: 'info',
  [CipaaStatus.COMPLETED]: 'success',
  [CipaaStatus.CANCELLED]: 'error',
};

export const CANDIDATE_STATUS_LABELS: Record<CipaaCandidateStatus, string> = {
  [CipaaCandidateStatus.PENDING]: 'Pendente',
  [CipaaCandidateStatus.APPROVED]: 'Aprovado',
  [CipaaCandidateStatus.REJECTED]: 'Rejeitado',
  [CipaaCandidateStatus.WITHDRAWN]: 'Desistente',
};

export const CANDIDATE_STATUS_COLORS: Record<CipaaCandidateStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [CipaaCandidateStatus.PENDING]: 'warning',
  [CipaaCandidateStatus.APPROVED]: 'success',
  [CipaaCandidateStatus.REJECTED]: 'error',
  [CipaaCandidateStatus.WITHDRAWN]: 'default',
};

export const CANDIDATE_POSITION_LABELS: Record<CipaaCandidatePosition, string> = {
  [CipaaCandidatePosition.TITULAR]: 'Titular',
  [CipaaCandidatePosition.SUPLENTE]: 'Suplente',
};

// ========== MAIN INTERFACES ==========

export interface Cipaa {
  id: number;
  companyId: number;
  name: string;
  accessKey: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  electionDate?: string;
  registrationOpenDate?: string;
  candidateAnnouncementDate?: string;
  resultsAnnouncementDate?: string;
  unionNotificationDate?: string;
  termStartDate?: string;
  termEndDate?: string;
  stabilityEndDate?: string;
  registrationLocation?: string;
  responsible?: string;
  effectiveMembers: number;
  alternateMembers: number;
  employeeCount?: number;
  riskLevel?: number;
  status: CipaaStatus;
  currentStep: number;
  createdAt: string;
  updatedAt: string;
  company?: {
    id: number;
    tradeName: string;
    legalName: string;
    cnpj: string;
  };
  _counts?: {
    candidates?: number;
    votes?: number;
    tokens?: number;
  };
}

export interface CipaaCandidate {
  id: number;
  cipaaId: number;
  employeeId: number;
  registrationDate: string;
  status: CipaaCandidateStatus;
  voteCount: number;
  isElected: boolean;
  position?: CipaaCandidatePosition;
  createdAt: string;
  updatedAt: string;
  employee?: {
    id: number;
    name: string;
    cpf: string;
    admissionDate?: string;
    occupation?: {
      id: number;
      name: string;
    };
    department?: {
      id: number;
      name: string;
    };
  };
}

export interface CipaaVote {
  id: number;
  cipaaId: number;
  candidateId: number;
  voterCpf: string;
  votedAt: string;
  createdAt: string;
}

export interface CipaaToken {
  id: number;
  cipaaId: number;
  token: string;
  isUsed: boolean;
  usedAt?: string;
  usedByCpf?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

// ========== FORM DATA ==========

export interface CipaaFormData {
  name: string;
  companyId: number | '';
  accessKey?: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  electionDate?: string;
  registrationOpenDate?: string;
  candidateAnnouncementDate?: string;
  resultsAnnouncementDate?: string;
  termStartDate?: string;
  termEndDate?: string;
  registrationLocation?: string;
  responsible?: string;
  effectiveMembers: number;
  alternateMembers: number;
  employeeCount?: number;
  riskLevel?: number;
}

// ========== REQUEST/RESPONSE ==========

export interface CreateCipaaRequest {
  name: string;
  companyId: number;
  accessKey?: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  electionDate?: string;
  registrationOpenDate?: string;
  candidateAnnouncementDate?: string;
  resultsAnnouncementDate?: string;
  termStartDate?: string;
  termEndDate?: string;
  registrationLocation?: string;
  responsible?: string;
  effectiveMembers?: number;
  alternateMembers?: number;
  employeeCount?: number;
  riskLevel?: number;
}

export interface UpdateCipaaRequest extends Partial<CreateCipaaRequest> {}

export interface CipaaFilters {
  search?: string;
  status?: CipaaStatus[];
  companyId?: number;
}

export interface CipaaListResponse {
  data: Cipaa[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CipaaElectionResults {
  election: Cipaa;
  totalVotes: number;
  totalVoters: number;
  candidates: Array<CipaaCandidate & {
    votePercentage: number;
  }>;
  elected: {
    effective: CipaaCandidate[];
    alternate: CipaaCandidate[];
  };
}

export interface CipaaVoterInfo {
  maskedCpf: string;
  votedAt: string;
}

// ========== PUBLIC VOTING ==========

export interface PublicElectionInfo {
  id: number;
  name: string;
  status: CipaaStatus;
  statusDescription: string;
  startDate: string;
  endDate: string;
  canRegisterCandidates: boolean;
  canVote: boolean;
  votingPeriod: string;
  company?: {
    tradeName?: string;
    legalName?: string;
  };
  // Legacy fields (may still be used)
  companyName?: string;
  startTime?: string;
  endTime?: string;
  effectiveMembers?: number;
  alternateMembers?: number;
}

export interface PublicCandidateInfo {
  id: number;
  name: string;
  occupation?: string;
  department?: string;
}

export interface VoteRequest {
  electionKey: string;
  candidateId: number;
  voterCpf: string;
}

// ========== TOKEN VALIDATION ==========

export interface TokenValidationResult {
  valid: boolean;
  message?: string;
  election?: {
    id: number;
    name: string;
    status: CipaaStatus;
    startDate: string;
    endDate: string;
    company?: {
      tradeName?: string;
      legalName?: string;
    };
  };
}

/**
 * Employee data returned by CPF validation
 * Backend returns employee directly via extractData
 */
export interface CpfValidationResult {
  id: number;
  name: string;
  cpf: string;
  occupation?: {
    id: number;
    name: string;
  } | string;
  department?: {
    id: number;
    name: string;
  } | string;
}

// ========== UTILITY FUNCTIONS ==========

export const formatCPF = (cpf: string): string => {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

export const removeCPFMask = (cpf: string): string => {
  return cpf.replace(/\D/g, '');
};

export const maskCPF = (cpf: string): string => {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return `${cleaned.substring(0, 3)}.***.***.${cleaned.substring(9, 11)}`;
};

export const formatDate = (date: string): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR');
};

export const formatDateTime = (date: string, time?: string): string => {
  if (!date) return '';
  const dateStr = formatDate(date);
  if (time) {
    return `${dateStr} às ${time.substring(0, 5)}`;
  }
  return dateStr;
};

export const getStatusLabel = (status: CipaaStatus): string => {
  return CIPAA_STATUS_LABELS[status] || status;
};

export const getStatusColor = (status: CipaaStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  return CIPAA_STATUS_COLORS[status] || 'default';
};
